<template>
  <div class="upload-template">
    <h4 style="text-align: center;">录取通知书模板配置</h4>

    <el-steps :active="active" finish-status="success" align-center style="margin: 20px 0 30px;">
      <el-step title="上传模板" description="上传录取通知书底图" />
      <el-step title="配置文字坐标" description="拖动姓名、设置字号方向" />
    </el-steps>

    <!-- 第一步：上传模板 -->
    <div v-show="active === 0" class="step-panel">
      <el-upload
        class="upload-demo"
        ref="upload"
        drag
        action="/upload_template"
        name="file"
        :with-credentials="true"
        :auto-upload="false"
        :show-file-list="true"
        :limit="1"
        accept=".jpg,.jpeg,.png"
        :before-upload="beforeUpload"
        :on-success="handleSuccess"
        :on-error="handleError"
        :on-change="handleFileChange"
      >
        <i class="el-icon-upload"></i>
        <div class="el-upload__text">将文件拖到此处，或<em>点击上传</em></div>
        <template #tip>
          <div class="el-upload__tip">jpg / png 图片，大小不超过 1024KB。若已存在模板将被覆盖。</div>
        </template>
      </el-upload>

      <div class="step-actions">
        <el-button type="primary" :loading="uploading" @click="submitUpload">
          {{ uploading ? '上传中...' : '上传并预览' }}
        </el-button>
        <el-button v-if="templateExists" @click="goToStep2">已有模板，直接配置坐标</el-button>
      </div>
    </div>

    <!-- 第二步：配置文字坐标 -->
    <div v-show="active === 1" class="step-panel">
      <div class="editor-toolbar">
        <span class="len-label">选择姓名字数：</span>
        <el-radio-group v-model="currentLen" @change="onLenChange">
          <el-radio-button :label="2">两字（张某）</el-radio-button>
          <el-radio-button :label="3">三字（张某某）</el-radio-button>
          <el-radio-button :label="4">四字（张某某某）</el-radio-button>
        </el-radio-group>
        <span class="sample-preview">当前示例：{{ sampleText }}</span>
        <el-button type="primary" @click="addMarker">插入姓名</el-button>
        <span class="tip-text">提示：分别为两字 / 三字 / 四字配置坐标，在图片上拖动姓名即可调整位置</span>
      </div>

      <div class="editor-body">
        <div class="canvas-wrap">
          <div class="image-container" ref="imageContainer">
            <img
              v-if="templateUrl"
              ref="templateImg"
              :src="templateUrl"
              class="template-img"
              @load="onImageLoad"
              draggable="false"
            />
            <div
              v-for="marker in currentMarkers"
              :key="marker.id"
              class="name-marker"
              :class="{ selected: marker.id === selectedId }"
              :style="markerStyle(marker)"
              @mousedown="startDrag(marker, $event)"
            >
              <template v-if="marker.ttype === '2'">
                <span v-for="(ch, i) in sampleChars" :key="i" class="v-char">{{ ch }}</span>
              </template>
              <template v-else>{{ sampleText }}</template>
            </div>
          </div>
        </div>

        <div class="prop-panel">
          <h5>属性设置</h5>
          <div v-if="selectedMarker">
            <div class="prop-row">
              <label>字号（像素）</label>
              <el-input-number v-model="selectedMarker.f_size" :min="8" :max="500" :step="2" />
            </div>
            <div class="prop-row">
              <label>排列方向</label>
              <el-radio-group v-model="selectedMarker.ttype">
                <el-radio label="1">横排</el-radio>
                <el-radio label="2">竖排</el-radio>
              </el-radio-group>
            </div>
            <div class="prop-row">
              <label>坐标</label>
              <span class="coord">X: {{ selectedMarker.x }}, Y: {{ selectedMarker.y }}</span>
            </div>
            <el-button type="danger" size="small" @click="removeMarker(selectedMarker.id)">
              删除该姓名
            </el-button>
          </div>
          <div v-else class="empty-tip">请点击“插入姓名”或选择一个姓名进行设置</div>
        </div>
      </div>

      <div class="step-actions">
        <el-button @click="active = 0">上一步</el-button>
        <el-button type="success" @click="submitPositions">
          提交
        </el-button>
      </div>
    </div>

    <!-- 上传后预览弹窗 -->
    <el-dialog v-model="previewVisible" title="模板预览" width="60%" align-center>
      <div class="preview-body">
        <img v-if="previewUrl" :src="previewUrl" style="max-width: 100%" />
      </div>
      <template #footer>
        <el-button @click="previewVisible = false">重新上传</el-button>
        <el-button type="primary" @click="confirmPreview">确认，下一步</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import { ElMessage } from 'element-plus';

let markerSeq = 0;

export default {
  data() {
    return {
      active: 0,
      templateExists: false,
      pendingFile: null,
      uploading: false,
      // 上传预览
      previewVisible: false,
      previewUrl: '',
      // 坐标配置
      templateUrl: '',
      naturalWidth: 0,
      naturalHeight: 0,
      scale: 1,
      currentLen: 2,
      markersByLen: { 2: [], 3: [], 4: [] },
      selectedId: null,
      draggingMarker: null,
      dragStart: null,
    };
  },
  computed: {
    sampleText() {
      return { 2: '张某', 3: '张某某', 4: '张某某某' }[this.currentLen] || '张某某';
    },
    currentMarkers() {
      return this.markersByLen[this.currentLen] || [];
    },
    selectedMarker() {
      return this.currentMarkers.find((m) => m.id === this.selectedId) || null;
    },
    sampleChars() {
      return this.sampleText.split('');
    },
  },
  mounted() {
    this.checkTemplateExists();
    this.loadCertFont();
    window.addEventListener('mousemove', this.onMouseMove);
    window.addEventListener('mouseup', this.onMouseUp);
    window.addEventListener('resize', this.updateScale);
  },
  beforeUnmount() {
    window.removeEventListener('mousemove', this.onMouseMove);
    window.removeEventListener('mouseup', this.onMouseUp);
    window.removeEventListener('resize', this.updateScale);
    if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
    if (this.templateUrl) URL.revokeObjectURL(this.templateUrl);
  },
  methods: {
    loadCertFont() {
      // 运行时加载生成通知书所用字体（SIMLI.TTF），避免构建时 webpack 解析 url
      if (window.__certFontLoaded) return;
      if (typeof FontFace === 'undefined') return;
      try {
        const font = new FontFace('SimliCert', "url('/api/font')");
        font
          .load()
          .then((loaded) => {
            document.fonts.add(loaded);
          })
          .catch(() => {});
        window.__certFontLoaded = true;
      } catch (e) {
        // 忽略字体加载失败，回退到默认字体
      }
    },
    async checkTemplateExists() {
      try {
        const res = await axios.get('/api/template/exists');
        this.templateExists = res.data.exists;
      } catch (e) {
        this.templateExists = false;
      }
    },
    beforeUpload(file) {
        const isImg = file.type === 'image/jpeg' || file.type === 'image/png';
        // 修改：10MB = 10 * 1024 KB
        const isLt10MB = file.size / 1024 < 10 * 1024;

        if (!isImg) ElMessage.error('上传图片只能是 JPG/PNG 格式!');
        if (!isLt10MB) ElMessage.error('上传图片大小不能超过 10MB!');

        return isImg && isLt10MB;
    },
    handleFileChange(uploadFile) {
      // 选择新文件后记录，并即时生成本地预览源
      this.pendingFile = uploadFile && uploadFile.raw ? uploadFile.raw : null;
    },
    submitUpload() {
      if (!this.pendingFile) {
        ElMessage.warning('请先选择要上传的模板文件');
        return;
      }
      this.uploading = true;
      this.$refs.upload.submit();
    },
    handleSuccess(response, uploadFile) {
      this.uploading = false;
      ElMessage.success('模板上传成功');
      this.templateExists = true;
      if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
      this.previewUrl = URL.createObjectURL(uploadFile.raw);
      this.previewVisible = true;
    },
    handleError() {
      this.uploading = false;
      ElMessage.error('模板上传失败');
    },
    confirmPreview() {
      this.previewVisible = false;
      this.goToStep2();
    },
    async goToStep2() {
      this.active = 1;
      await this.loadTemplateImage();
      await this.loadPositions();
    },
    async loadTemplateImage() {
      try {
        const res = await axios.get('/api/template', { responseType: 'blob' });
        if (this.templateUrl) URL.revokeObjectURL(this.templateUrl);
        this.templateUrl = URL.createObjectURL(res.data);
      } catch (e) {
        ElMessage.error('加载模板图片失败');
      }
    },
    async loadPositions() {
      try {
        const res = await axios.get('/api/text-positions');
        const byLen = { 2: [], 3: [], 4: [] };
        (res.data || []).forEach((p) => {
          let len = Number(p.name_len) || 3;
          len = Math.min(Math.max(len, 2), 4);
          byLen[len].push({
            id: ++markerSeq,
            x: Number(p.x) || 0,
            y: Number(p.y) || 0,
            ttype: String(p.ttype || '1'),
            f_size: Number(p.f_size) || 40,
          });
        });
        this.markersByLen = byLen;
        this.selectedId = this.currentMarkers.length ? this.currentMarkers[0].id : null;
      } catch (e) {
        this.markersByLen = { 2: [], 3: [], 4: [] };
      }
    },
    onLenChange() {
      this.selectedId = this.currentMarkers.length ? this.currentMarkers[0].id : null;
    },
    onImageLoad() {
      const img = this.$refs.templateImg;
      this.naturalWidth = img.naturalWidth;
      this.naturalHeight = img.naturalHeight;
      this.updateScale();
    },
    updateScale() {
      const img = this.$refs.templateImg;
      if (img && this.naturalWidth) {
        this.scale = img.clientWidth / this.naturalWidth;
      }
    },
    addMarker() {
      if (!this.naturalWidth) {
        ElMessage.warning('模板图片尚未加载完成');
        return;
      }
      const marker = {
        id: ++markerSeq,
        x: Math.round(this.naturalWidth * 0.3),
        y: Math.round(this.naturalHeight * 0.3),
        ttype: '1',
        f_size: Math.max(20, Math.round(this.naturalWidth * 0.05)),
      };
      this.currentMarkers.push(marker);
      this.selectedId = marker.id;
    },
    removeMarker(id) {
      this.markersByLen[this.currentLen] = this.currentMarkers.filter((m) => m.id !== id);
      if (this.selectedId === id) {
        this.selectedId = this.currentMarkers.length ? this.currentMarkers[0].id : null;
      }
    },
    markerStyle(marker) {
      return {
        left: marker.x * this.scale + 'px',
        top: marker.y * this.scale + 'px',
        fontSize: marker.f_size * this.scale + 'px',
      };
    },
    startDrag(marker, e) {
      this.selectedId = marker.id;
      this.draggingMarker = marker;
      this.dragStart = { mouseX: e.clientX, mouseY: e.clientY, x: marker.x, y: marker.y };
      e.preventDefault();
    },
    onMouseMove(e) {
      if (!this.draggingMarker) return;
      const dx = (e.clientX - this.dragStart.mouseX) / this.scale;
      const dy = (e.clientY - this.dragStart.mouseY) / this.scale;
      let nx = this.dragStart.x + dx;
      let ny = this.dragStart.y + dy;
      nx = Math.max(0, Math.min(nx, this.naturalWidth));
      ny = Math.max(0, Math.min(ny, this.naturalHeight));
      this.draggingMarker.x = Math.round(nx);
      this.draggingMarker.y = Math.round(ny);
    },
    onMouseUp() {
      this.draggingMarker = null;
    },
    async submitPositions() {
      const positions = [];
      [2, 3, 4].forEach((len) => {
        (this.markersByLen[len] || []).forEach((m) => {
          positions.push({
            x: m.x,
            y: m.y,
            ttype: m.ttype,
            f_size: m.f_size,
            name_len: len,
          });
        });
      });
      const emptyLens = [2, 3, 4].filter((len) => (this.markersByLen[len] || []).length === 0);
      if (emptyLens.length) {
        ElMessage.warning(`还有 ${emptyLens.join('、')} 字姓名未配置坐标，请切换字数逐一配置`);
        return;
      }
      try {
        await axios.post('/api/text-positions', { positions });
        ElMessage.success('配置提交成功');
      } catch (e) {
        ElMessage.error('提交失败，请重试');
      }
    },
  },
};
</script>

<style scoped>
.upload-template {
  max-width: 1000px;
  margin: 0 auto;
  padding: 16px;
}
.step-panel {
  margin-top: 10px;
}
.step-actions {
  text-align: center;
  margin-top: 20px;
}
.editor-toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 12px;
}
.tip-text {
  color: #909399;
  font-size: 13px;
}
.len-label {
  font-size: 14px;
  color: #303133;
}
.sample-preview {
  font-size: 14px;
  color: #409eff;
  font-weight: 600;
}
.editor-body {
  display: flex;
  gap: 16px;
  align-items: flex-start;
}
.canvas-wrap {
  flex: 1;
  min-width: 0;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 8px;
  overflow: auto;
  max-height: 70vh;
}
.image-container {
  position: relative;
  display: inline-block;
  line-height: 0;
}
.template-img {
  max-width: 100%;
  display: block;
  user-select: none;
}
.name-marker {
  position: absolute;
  cursor: move;
  color: #000;
  font-family: 'SimliCert', serif;
  line-height: 1;
  white-space: nowrap;
  padding: 2px;
  border: 1px dashed transparent;
  user-select: none;
}
.name-marker.selected {
  border-color: #409eff;
  background: rgba(64, 158, 255, 0.08);
}
.name-marker .v-char {
  display: block;
  text-align: center;
}
.prop-panel {
  width: 260px;
  flex-shrink: 0;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 16px;
}
.prop-panel h5 {
  margin: 0 0 16px;
}
.prop-row {
  margin-bottom: 16px;
}
.prop-row label {
  display: block;
  margin-bottom: 6px;
  color: #606266;
  font-size: 13px;
}
.coord {
  color: #303133;
}
.empty-tip {
  color: #909399;
  font-size: 13px;
}
.preview-body {
  text-align: center;
  max-height: 70vh;
  overflow-y: auto;
}
.el-upload__text em {
  color: #409eff;
  cursor: pointer;
}
</style>
